/**
 * Android hardware/gesture back button handling (native only).
 *
 * Capacitor's `backButton` event reports `canGoBack` from the *WebView's*
 * history, which is not a useful signal for this SPA: an authenticated user
 * sitting on `/dashboard` still has entries behind them, and walking back into
 * one bounces straight off an auth guard, which re-pushes `/dashboard`.
 * Measured on device (Galaxy S24+, build 101): one back press produced
 * `pushState → /dashboard` followed by `popstate → /dashboard`, with
 * `history.length` pinned at 3 across five presses. So `canGoBack` never goes
 * false, the app never exits, and the user just sees the home page reload.
 *
 * Fix: decide from the *app's* route instead. On a root route, back exits the
 * app (guarded by a press-again confirmation so it can't happen by accident);
 * anywhere else it behaves normally.
 *
 * `window.location.pathname` is read at press time rather than captured from
 * `useLocation()`, so the listener — registered once — can never act on a stale
 * route.
 */

import { useEffect, useRef } from 'react';
import { App } from '@capacitor/app';
import type { PluginListenerHandle } from '@capacitor/core';
import { isNative } from '@/utils/platform';
import { useToast } from '@/contexts/ToastContext';

/** Bottom of the in-app navigation stack: logged-out landing and logged-in home. */
const ROOT_ROUTES = ['/', '/dashboard'];

/** How long a first back press stays "armed" for the confirming second press. */
const EXIT_CONFIRM_WINDOW_MS = 2000;

export function useAndroidBackButton(): void {
  const { showToast, dismiss } = useToast();
  const armedRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Id of the confirmation toast, so it is dismissed by identity rather than by position.
  const toastIdRef = useRef<string | undefined>(undefined);
  // The listener is registered once, so it must not close over stale context callbacks.
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;
  const dismissRef = useRef(dismiss);
  dismissRef.current = dismiss;

  useEffect(() => {
    if (!isNative) return;

    /** Drop the pending confirmation: flag, timer and toast together. */
    const disarm = () => {
      armedRef.current = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = undefined;
      }
      if (toastIdRef.current) {
        dismissRef.current(toastIdRef.current);
        toastIdRef.current = undefined;
      }
    };

    const handleBack = () => {
      if (!ROOT_ROUTES.includes(window.location.pathname)) {
        window.history.back();
        return;
      }

      if (armedRef.current) {
        // A TRUE exit — the Activity is finished and the process torn down.
        //
        // This was briefly minimizeApp(), so that back-to-exit would produce the background
        // transition @capgo/capacitor-updater needs to apply a staged bundle. It worked, but it
        // silently changed what "exit" means: the app stopped closing, reopening became a warm
        // resume, and state that used to be discarded now survived (the confirmation toast below
        // was the first symptom, not the only possible one).
        //
        // Reverted 2026-08-15. The update problem is solved in the right layer instead:
        // `autoUpdate: "onLaunch"` in capacitor.config.ts applies a pending bundle during the
        // next cold start. That fits how updates actually reach people — most are published while
        // the app is not running at all — and it keeps exit meaning exit.
        //
        // The cleanup below still runs: arming the prompt and then leaving via Home (which does
        // NOT tear the app down) would otherwise resume into a stale toast.
        disarm();
        App.exitApp();
        return;
      }

      armedRef.current = true;
      toastIdRef.current = showToastRef.current('info', 'Press back again to exit');
      timerRef.current = setTimeout(disarm, EXIT_CONFIRM_WINDOW_MS);
    };

    let handle: PluginListenerHandle | undefined;
    let stateHandle: PluginListenerHandle | undefined;
    let cancelled = false;
    App.addListener('backButton', handleBack).then((h) => {
      // Unmounted before the listener resolved — drop it rather than leak it.
      if (cancelled) h.remove();
      else handle = h;
    });

    // The app now SURVIVES being left, so a half-finished confirmation survives with it. Arming
    // the prompt and then leaving via Home or the app switcher never reaches handleBack, and the
    // 2s timer cannot rescue it either: WebView timers are throttled while backgrounded, so the
    // toast is frozen mid-life rather than expiring. Clear it on the way back in, so a resume is
    // always clean.
    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) disarm();
    }).then((h) => {
      if (cancelled) h.remove();
      else stateHandle = h;
    });

    return () => {
      cancelled = true;
      handle?.remove();
      stateHandle?.remove();
      disarm();
    };
  }, []);
}
